/**
 * O que faz: lê e altera a coluna de Kanban que exige agendamento de retorno na clínica ativa.
 * Onde usa: ReturnsBoard para configuração e PipelineBoard para decidir se o drop abre o modal.
 * Conexões: React Query, useClinica e RPC configure_return_kanban_stage.
 * Tabelas: return_kanban_settings (SELECT via RLS); escrita apenas por RPC security definer.
 * Data: 27/08/2026.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useClinica } from '@/contexts/ClinicaContext';
import { supabase } from '@/integrations/supabase/client';
import type { ReturnKanbanSettings } from './returnTypes';

interface UseReturnKanbanSettingsOptions {
  enabled?: boolean;
}

export const returnKanbanSettingsQueryKeys = {
  all: ['return-kanban-settings'] as const,
  clinic: (clinicaId: string) => ['return-kanban-settings', clinicaId] as const,
};

export const useReturnKanbanSettings = ({ enabled = true }: UseReturnKanbanSettingsOptions = {}) => {
  const { clinicaAtiva } = useClinica();

  return useQuery({
    queryKey: clinicaAtiva?.id
      ? returnKanbanSettingsQueryKeys.clinic(clinicaAtiva.id)
      : [...returnKanbanSettingsQueryKeys.all, 'disabled'] as const,
    enabled: enabled && Boolean(clinicaAtiva?.id),
    queryFn: async (): Promise<ReturnKanbanSettings | null> => {
      if (!clinicaAtiva?.id) {
        return null;
      }

      // Tabela: return_kanban_settings | Operação: SELECT.
      // Campos: clinica_id e etapa_kanban_id, apenas para saber qual drop exige agendamento.
      // Filtros: clinica_id ativa; a RLS repete a mesma fronteira no servidor.
      // Risco: a configuração revela somente um UUID de etapa, sem dados pessoais de leads.
      const { data, error } = await supabase
        .from('return_kanban_settings' as any)
        .select('clinica_id, etapa_kanban_id')
        .eq('clinica_id', clinicaAtiva.id)
        .maybeSingle();

      if (error) {
        throw new Error('Não foi possível carregar a coluna de Kanban vinculada aos retornos.');
      }

      return data as ReturnKanbanSettings | null;
    },
    staleTime: 60 * 1000,
  });
};

export const useConfigureReturnKanbanStage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (etapaKanbanId: string | null) => {
      // Motivo: null representa uma decisão explícita de remover a integração; qualquer valor preenchido é validado no servidor contra a clínica ativa.
      if (etapaKanbanId !== null && !etapaKanbanId.trim()) {
        throw new Error('Selecione uma coluna válida ou escolha remover o vínculo.');
      }

      const { error } = await supabase.rpc('configure_return_kanban_stage' as any, {
        p_etapa_kanban_id: etapaKanbanId,
      });

      if (error) {
        throw new Error('Não foi possível atualizar a coluna vinculada aos retornos.');
      }
    },
    onSuccess: (_, etapaKanbanId) => {
      queryClient.invalidateQueries({ queryKey: returnKanbanSettingsQueryKeys.all });
      toast.success(
        etapaKanbanId
          ? 'Coluna de retorno vinculada ao Kanban.'
          : 'Vínculo com a coluna de Kanban removido.',
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Não foi possível atualizar a coluna de retorno.');
    },
  });
};
